import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Stethoscope, Check, X, AlertTriangle, ChevronRight, RefreshCw, Loader2, FileJson,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supplierReconciliationApi } from '../lib/api';

function JsonBlock({ data, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const json = JSON.stringify(data, null, 2);
    return (
        <div className="border rounded-md bg-slate-50 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full px-3 py-2 flex items-center justify-between text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
                <span className="flex items-center gap-2">
                    <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} />
                    <FileJson className="h-3.5 w-3.5" />
                    {json.length} chars
                </span>
                <span className="text-slate-400">{open ? 'Hide' : 'Show'} JSON</span>
            </button>
            {open && (
                <pre className="text-xs font-mono bg-slate-900 text-slate-100 p-3 overflow-x-auto max-h-96">
                    {json}
                </pre>
            )}
        </div>
    );
}

function FieldRow({ label, draft, booked }) {
    const status = (info) => {
        if (!info) return null;
        if (info.no_sample) return <Badge variant="outline" className="text-xs bg-slate-100 text-slate-500 border-slate-200">no sample</Badge>;
        if (info.present) {
            return (
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                        <Check className="h-3 w-3 mr-1" /> Present
                    </Badge>
                    {info.via_key && (
                        <code className="text-xs font-mono text-slate-500">via <span className="text-slate-700">{info.via_key}</span>{info.via_nested ? ' (nested)' : ''}{info.found_on ? ` (on ${info.found_on})` : ''}</code>
                    )}
                </div>
            );
        }
        return (
            <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">
                <X className="h-3 w-3 mr-1" /> Not exposed
            </Badge>
        );
    };
    return (
        <div className="grid grid-cols-12 gap-3 py-3 border-b border-slate-100">
            <div className="col-span-3 text-sm font-medium text-slate-800">{label}</div>
            <div className="col-span-2 text-xs text-slate-500 font-mono">
                {(draft?.asked_keys || booked?.asked_keys || []).join(' / ')}
            </div>
            <div className="col-span-3">{status(draft)}</div>
            <div className="col-span-3">{status(booked)}</div>
            <div className="col-span-1 text-xs text-slate-400 truncate">
                {draft?.value_preview && (
                    <span title={draft.value_preview}>{draft.value_preview.slice(0, 30)}</span>
                )}
            </div>
        </div>
    );
}

function SubResource({ sub }) {
    const statusColor = sub.status === 'ok' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
        : sub.status === 'fail' ? 'text-rose-700 bg-rose-50 border-rose-200'
            : 'text-slate-500 bg-slate-50 border-slate-200';
    return (
        <div className="border border-slate-200 rounded-md p-3 bg-card">
            <div className="flex items-start justify-between mb-1.5">
                <div className="font-mono text-sm text-slate-800">{sub.label}</div>
                <Badge variant="outline" className={`text-xs ${statusColor}`}>
                    {sub.status}
                </Badge>
            </div>
            {sub.url && (
                <code className="text-xs text-slate-500 break-all block mb-2">{sub.url}</code>
            )}
            {sub.error && (
                <div className="text-xs text-rose-600 mb-2">{sub.error}</div>
            )}
            {sub.data && <JsonBlock data={sub.data} />}
        </div>
    );
}

export default function ReconciliationDiagnosticPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';

    const [supplierNumber, setSupplierNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const fetchData = async (sn) => {
        if (!isSuperAdmin) return;
        setLoading(true);
        try {
            const { data: d } = await supplierReconciliationApi.diagnostic(sn);
            setData(d);
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            toast.error(`Failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSuperAdmin) fetchData(supplierNumber);
    // eslint-disable-next-line
    }, []);

    if (!isSuperAdmin) {
        return (
            <div className="min-h-screen bg-slate-50">
                <Header />
                <main className="max-w-3xl mx-auto px-6 py-16 text-center">
                    <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-3" />
                    <h2 className="text-xl font-display font-semibold text-slate-900">Super admin only</h2>
                    <p className="text-sm text-slate-600 mt-2">This diagnostic exposes raw IBAN/BIC and other sensitive data.</p>
                </main>
            </div>
        );
    }

    const draft = data?.draft;
    const booked = data?.booked;
    const inventory = data?.field_inventory || {};

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1300px] mx-auto px-6 py-8">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                            <Stethoscope className="h-4 w-4" />
                            <span>Finance · Diagnostics</span>
                        </div>
                        <h1 className="text-3xl font-display font-semibold text-slate-900">
                            e-conomic Payload Inspector
                        </h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                            Inspect raw e-conomic supplier-invoice payloads (one DRAFT + one BOOKED) for a chosen supplier.
                            Use this to confirm what fields the e-conomic REST API actually exposes
                            <strong> before</strong> investing in OCR / line-level matching.
                        </p>
                    </div>
                </div>

                <Card className="mb-4 bg-card">
                    <CardContent className="p-4 flex items-end gap-3">
                        <div className="flex-1 max-w-xs">
                            <label className="text-xs font-medium text-slate-600 mb-1 block">e-conomic supplier number</label>
                            <Input
                                type="number"
                                value={supplierNumber}
                                onChange={(e) => setSupplierNumber(parseInt(e.target.value || 1, 10))}
                                placeholder="1 = Sony DADC"
                                data-testid="diag-supplier-input"
                            />
                        </div>
                        <Button onClick={() => fetchData(supplierNumber)} disabled={loading} data-testid="diag-fetch-btn">
                            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                            Fetch
                        </Button>
                        {data?.supplier?.name && (
                            <div className="ml-auto text-right">
                                <div className="text-xs text-slate-500">Supplier</div>
                                <div className="font-medium text-slate-900">{data.supplier.name}</div>
                                <div className="text-xs text-slate-500">{data.supplier.country} · {data.supplier.currency}</div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {loading && <div className="py-16 flex justify-center"><LoadingSpinner /></div>}

                {data && !loading && (
                    <>
                        {/* Field inventory */}
                        <Card className="mb-6 bg-card">
                            <CardHeader>
                                <CardTitle className="text-base">Field Inventory — what e-conomic actually exposes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-12 gap-3 pb-2 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 font-medium">
                                    <div className="col-span-3">Field</div>
                                    <div className="col-span-2">Looked for</div>
                                    <div className="col-span-3">Draft</div>
                                    <div className="col-span-3">Booked</div>
                                    <div className="col-span-1">Preview</div>
                                </div>
                                {Object.entries(inventory).map(([label, info]) => (
                                    <FieldRow key={label} label={label.replace(/_/g, ' ')} draft={info.draft} booked={info.booked} />
                                ))}
                            </CardContent>
                        </Card>

                        {/* DRAFT sample */}
                        <Card className="mb-6 bg-card">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">DRAFT</Badge>
                                    {draft ? (
                                        <>Journal {draft.journal_number} · Voucher {draft.voucher_number}</>
                                    ) : 'No draft found'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {draft ? (
                                    <>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Entry keys ({draft.entry_keys.length})</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {draft.entry_keys.map((k) => (
                                                    <code key={k} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{k}</code>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Raw entry</div>
                                            <JsonBlock data={draft.raw_entry} defaultOpen />
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Raw voucher (parent)</div>
                                            <JsonBlock data={draft.raw_voucher} />
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Sub-resources probed ({draft.sub_resources?.length || 0})</div>
                                            <div className="space-y-2">
                                                {(draft.sub_resources || []).map((s, idx) => (
                                                    <SubResource key={idx} sub={s} />
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : <div className="text-sm text-slate-500">No draft supplier invoice in any journal for this supplier.</div>}
                            </CardContent>
                        </Card>

                        {/* BOOKED sample */}
                        <Card className="mb-6 bg-card">
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">BOOKED</Badge>
                                    {booked ? (
                                        <>Year {booked.accounting_year} · Voucher {booked.voucher_number}</>
                                    ) : 'No booked entry found'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {booked ? (
                                    <>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Entry keys ({booked.entry_keys.length})</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {booked.entry_keys.map((k) => (
                                                    <code key={k} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{k}</code>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Raw entry</div>
                                            <JsonBlock data={booked.raw_entry} defaultOpen />
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Sub-resources probed ({booked.sub_resources?.length || 0})</div>
                                            <div className="space-y-2">
                                                {(booked.sub_resources || []).map((s, idx) => (
                                                    <SubResource key={idx} sub={s} />
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : <div className="text-sm text-slate-500">No booked supplier invoice in any accounting year for this supplier.</div>}
                            </CardContent>
                        </Card>

                        {/* Summary callout */}
                        {data.summary && (
                            <Card className="bg-amber-50/40 border-amber-200">
                                <CardHeader>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                                        Summary — what we can use for matching
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="text-sm text-slate-700 space-y-1.5">
                                        <li className="flex gap-2"><span className={data.summary.has_invoice_lines ? 'text-emerald-600' : 'text-rose-600'}>{data.summary.has_invoice_lines ? '✓' : '✗'}</span>Invoice lines</li>
                                        <li className="flex gap-2"><span className={data.summary.has_po_number_field ? 'text-emerald-600' : 'text-rose-600'}>{data.summary.has_po_number_field ? '✓' : '✗'}</span>PO-number field</li>
                                        <li className="flex gap-2"><span className={data.summary.has_project_reference ? 'text-emerald-600' : 'text-rose-600'}>{data.summary.has_project_reference ? '✓' : '✗'}</span>Project reference</li>
                                        <li className="flex gap-2"><span className={data.summary.has_dimensions ? 'text-emerald-600' : 'text-rose-600'}>{data.summary.has_dimensions ? '✓' : '✗'}</span>Dimensions / department</li>
                                        <li className="flex gap-2"><span className={data.summary.has_comments ? 'text-emerald-600' : 'text-rose-600'}>{data.summary.has_comments ? '✓' : '✗'}</span>Comments / notes</li>
                                        <li className="flex gap-2"><span className={data.summary.has_attachment_url ? 'text-amber-600' : 'text-rose-600'}>{data.summary.has_attachment_url ? '~' : '✗'}</span>Attachment URL on voucher{data.summary.has_attachment_url && !data.summary.attachment_url_actually_works ? ' (URL present but returns 404)' : ''}</li>
                                    </ul>
                                    <div className="mt-3 pt-3 border-t border-amber-200">
                                        <div className="text-xs font-medium text-slate-700 mb-1">Fields we CAN text-search for matching:</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(data.summary["fields_we_can_text-search_for_matching"] || []).map((f) => (
                                                <code key={f} className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">{f}</code>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
